import { getDiscountPriceWithDefault, getVariantWeightPrice } from "./product";
import {
  DELIVERY_OPTIONS,
  DELIVERY_REGION,
  FREE_DELIVERY_FROM_PRICE,
  MINIMUM_ORDER_AMOUNT,
  REGIONAL_FREE_DELIVERY_FROM_PRICE,
  REGIONAL_SEND_COST,
  SEND_COST,
  getDeliveryTimeSlotSurcharge,
} from "../constants/DeliveryConstants";
import { deliveryRegionFor } from "./region";

/**
 * Zitten er producten in de winkelwagen die al in de aanbieding zijn? Een kortingscode met
 * `applyOnlyToNonDiscountedProducts` slaat die producten over (zie de skip hieronder in
 * calculateDiscountPercentageTotalAmount), waardoor de korting lager uitvalt dan het
 * percentage van de code doet vermoeden. Wordt gebruikt om dat aan de klant uit te leggen -
 * zie DiscountCodeField.
 */
export const cartHasDiscountedProducts = (cartItems) =>
  !!cartItems?.some((product) => product.discount > 0);

export const calculateDiscountPercentageTotalAmount = (
  cartItems,
  discountCode,
) => {
  let totalDiscountAmount = 0;
  cartItems.forEach((product) => {
    // Skip products with discount if code only applies to non-discounted products
    if (discountCode.applyOnlyToNonDiscountedProducts && product.discount > 0) {
      return;
    }

    let productPrice = getVariantWeightPrice(product);
    let discountPrice = getDiscountPriceWithDefault(
      productPrice,
      product.discount,
    );
    let discountAmountWithCode = calculateDiscountWithPercentage(
      discountPrice,
      discountCode.discountPercentage,
    );
    totalDiscountAmount += discountAmountWithCode * product.quantity;
  });
  return roundPriceTwoDecimals(totalDiscountAmount);
};

export const calculatePickupDiscountAmount = (cartItems) => {
  let total = 0;
  cartItems.forEach((product) => {
    if (product.discount > 0) return;
    const productPrice = getVariantWeightPrice(product);
    total += productPrice * product.quantity;
  });
  return roundPriceTwoDecimals(total * 0.03);
};

export const calculateDiscountAmountTotalAmount = (cartItems, discountCode) => {
  return discountCode.discountAmount;
};

export const calculateDiscountWithPercentage = (price, percentage) => {
  return (price * percentage) / 100;
};

export const roundPriceTwoDecimals = (price) => {
  return Math.round(price * 100) / 100;
};

/**
 * De verzendkosten in euro's, of `null` zolang de postcode onbekend is.
 *
 * Twee tarieven, naar de bezorgregio (`deliveryRegionFor`):
 * - West-Friesland (de eigen bus, HVC-83): `REGIONAL_SEND_COST` (EUR 4,95),
 *   gratis vanaf `REGIONAL_FREE_DELIVERY_FROM_PRICE` (EUR 100). Geen
 *   tijdvaktoeslag: een regionale klant kiest geen tijdvak.
 * - de rest van Nederland en Belgie: `SEND_COST` (EUR 9,95), gratis vanaf
 *   `FREE_DELIVERY_FROM_PRICE` (EUR 150), plus de toeslag op een smal tijdvak.
 *
 * Zonder postcode is de regio onbekend en dus ook het bedrag: `null`. De
 * winkelwagen vraagt dan om de postcode, zoals voor SooCool (besluit
 * 2026-10-15; van 10 tot 15 oktober 2026 was er een tarief voor iedereen en
 * toonde de wagen het meteen). Of we op een adres bezorgen (Waddeneilanden,
 * Wallonie) is een andere vraag, en die stelt de checkout.
 *
 * Afhalen kost NOOIT verzendkosten. Dat hoort in deze functie en niet bij de
 * aanroeper: zolang de bezorgmethode buiten de berekening bleef, toonde de
 * winkelwagen een bedrag dat in de checkout weer verdween.
 *
 * @param {number} totalItemsPrice Productsubtotaal, exclusief cadeaubonnen.
 * @param {number} totalGiftcardsPrice Cadeaubonsubtotaal. Telt niet mee voor de
 *   drempel; staat er nog in omdat alle aanroepers hem doorgeven en het bedrag
 *   de betekenis van `totalItemsPrice === 0` leesbaar houdt.
 * @param {{zip: string|number, country: string}} deliveryValues Het bezorgadres:
 *   bepaalt de regio, en daarmee het tarief.
 * @param {string} deliveryMethod `DELIVERY_OPTIONS.SEND` of `.PICKUP`. De
 *   winkelwagen kent de keuze nog niet - daar wordt bewust `SEND` doorgegeven,
 *   met een tekst erbij dat afhalen gratis is.
 * @param {string|null} timeSlot Het gekozen levervenster als contractlabel
 *   (`DELIVERY_TIME_SLOTS[].label`). De winkelwagen kent dat nog niet en geeft
 *   niets mee; dan geldt het basistarief, zoals de afhaal-notitie daar ook al
 *   werkt. Een onbekend label kost nooit toeslag.
 */
export const calculateShippingCost = (
  totalItemsPrice,
  totalGiftcardsPrice,
  deliveryValues,
  deliveryMethod = DELIVERY_OPTIONS.SEND,
  timeSlot = null,
) => {
  // Afhalen kost nooit verzendkosten.
  if (deliveryMethod === DELIVERY_OPTIONS.PICKUP) return 0;

  // Alleen cadeaubonnen in de wagen: er valt niets te verzenden.
  if (totalItemsPrice === 0) return 0;

  const region = deliveryRegionFor(deliveryValues);
  if (region === null) return null;

  // West-Friesland: de eigen bus, een vast bedrag en geen tijdvak.
  if (region === DELIVERY_REGION.REGIONAL) {
    return totalItemsPrice >= REGIONAL_FREE_DELIVERY_FROM_PRICE
      ? 0
      : REGIONAL_SEND_COST;
  }

  /**
   * De toeslag op de twee smalle vensters (08:00-12:00 en 12:00-17:00).
   *
   * Hij komt BOVENOP het bezorgtarief en vervalt NIET boven de gratis-drempel.
   * Dat is geen slordigheid maar de afspraak: gratis bezorging is een cadeau op
   * het BEZORGEN, en een smaller venster is een premium op de bezorging zelf -
   * SooCool rekent ons die EUR 1,00 exclusief btw ook bij een order van EUR 300.
   *
   * Gevolg, en dat moet de samenvatting eerlijk tonen: een klant boven EUR 150
   * ziet "gratis bezorging" staan met EUR 1,25 ernaast. Zie
   * `formatShippingCost` in `useCheckoutState`, waar die twee als een regel met
   * een toelichting getoond worden in plaats van als een raadsel.
   */
  const surcharge = getDeliveryTimeSlotSurcharge(timeSlot);

  if (totalItemsPrice >= FREE_DELIVERY_FROM_PRICE) {
    return roundPriceTwoDecimals(surcharge);
  }

  return roundPriceTwoDecimals(SEND_COST + surcharge);
};

/**
 * Hoeveel er nog bij moet om te mogen laten BEZORGEN, gemeten over het
 * productsubtotaal. 0 betekent: het minimum is gehaald (of de wagen is leeg).
 *
 * Bestaat zodat de winkelwagen dezelfde grens kan tonen als de checkout. Tot nu
 * toe kende alleen de checkout het minimum, dus een wagen van EUR 40 kreeg
 * netjes "Verzendkosten EUR 9,95" te zien en liep pas bij het afrekenen vast.
 * Cadeaubonnen tellen niet mee - vandaar het productsubtotaal en niet het
 * wagentotaal.
 */
export const calculateRemainingForMinimumOrder = (totalItemsPrice) => {
  if (!(totalItemsPrice > 0)) return 0;
  const remaining = MINIMUM_ORDER_AMOUNT - totalItemsPrice;
  return remaining > 0 ? roundPriceTwoDecimals(remaining) : 0;
};

/**
 * Hoeveel er nog bij moet voor gratis bezorging. 0 betekent: geen boodschap
 * tonen - al gratis, of het adres is nog onbekend.
 *
 * De drempel volgt de regio: EUR 100 in West-Friesland, EUR 150 daarbuiten
 * (Belgie inbegrepen).
 *
 * @param {{zip: string|number, country: string}} deliveryValues Het bezorgadres.
 */
export const calculateRemainingForFreeShipping = (
  shippingCost,
  totalItemsPrice,
  deliveryValues,
) => {
  if (shippingCost == null || shippingCost === 0) return 0;
  if (totalItemsPrice <= 0) return 0;
  const region = deliveryRegionFor(deliveryValues);
  if (region === null) return 0;
  const threshold =
    region === DELIVERY_REGION.REGIONAL
      ? REGIONAL_FREE_DELIVERY_FROM_PRICE
      : FREE_DELIVERY_FROM_PRICE;
  const remaining = threshold - totalItemsPrice;
  return remaining > 0 ? roundPriceTwoDecimals(remaining) : 0;
};
